import { useCallback, useContext, useEffect, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useParams } from "react-router";
import { useMount, useUnmount } from "react-use";

import useFAST from "./FASTContext";
import { WebSocketContext } from "./WebSocket";
import { API_DATE_FORMAT } from "../constants/dateFormat";
import useTimebounds from "../hooks/useTimebounds";
import { getCurrentTimelineQuery } from "../utils/fast";

import type { JSX } from "react";

import type { FASTChannelRoutesParams } from "../components/routes/FASTChannelRoutes";

export interface ScheduleLockEvent {
  is_locked?: boolean;
  is_owned: boolean;
  owner_info?: {
    user_uuid: string;
    first_name: string;
    last_name: string;
    avatar: string;
  };
}

interface ScheduleUpdatedEvent {
  start_date: string;
}

interface FASTWebSocketProviderProps {
  children: React.ReactElement;
}

export default function FASTWebSocketProvider({
  children,
}: FASTWebSocketProviderProps): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { dispatch } = useFAST();
  const queryClient = useQueryClient();
  const { getCurrentDay } = useTimebounds();

  const channelTopic = `fast_schedule:${channelId}`;

  const handleScheduleLock = useCallback(
    ({ is_locked, is_owned, owner_info }: ScheduleLockEvent) => {
      dispatch({
        type: "LOCK_SCHEDULE",
        isLocked: is_locked,
        isOwned: is_owned,
        lockingAccount: owner_info
          ? {
              id: owner_info?.user_uuid,
              firstName: owner_info?.first_name,
              lastName: owner_info?.last_name,
              avatar: owner_info?.avatar,
            }
          : undefined,
      });
    },
    [dispatch],
  );

  const { channels, joinChannel, leaveChannel } = useContext(WebSocketContext);

  const channel = useMemo(
    () => channels?.[channelTopic] ?? null,
    [channels, channelTopic],
  );

  const invalidateTimelineQueries = useCallback(
    (currentDay: string) => {
      const { previousQuery, currentQuery, nextQuery } =
        getCurrentTimelineQuery({
          currentDay,
          channelId,
        });

      queryClient.invalidateQueries(previousQuery);
      queryClient.invalidateQueries(currentQuery);
      queryClient.invalidateQueries(nextQuery);
    },
    [channelId, queryClient],
  );

  const handleScheduleRelease = useCallback(() => {
    dispatch({
      type: "RELEASE_SCHEDULE",
    });

    invalidateTimelineQueries(getCurrentDay());
  }, [dispatch, invalidateTimelineQueries, getCurrentDay]);

  const handleError = useCallback((reason?: unknown) => {
    console.error("Error on channel", reason);
  }, []);

  const handleScheduleUpdate = useCallback(
    ({ start_date }: ScheduleUpdatedEvent) => {
      const currentDay = moment(start_date).format(API_DATE_FORMAT);
      invalidateTimelineQueries(currentDay);
    },
    [invalidateTimelineQueries],
  );

  useEffect(() => {
    if (!channel) return () => {};

    const scheduledLockedRef = channel.on(
      "schedule_locked",
      handleScheduleLock,
    );
    const scheduledReleasedRef = channel.on(
      "schedule_released",
      handleScheduleRelease,
    );
    const scheduledUpdatedRef = channel.on(
      "schedule_updated",
      handleScheduleUpdate,
    );
    channel.onError(handleError);

    return () => {
      channel.off("schedule_locked", scheduledLockedRef);
      channel.off("schedule_released", scheduledReleasedRef);
      channel.off("schedule_updated", scheduledUpdatedRef);
    };
  }, [
    channel,
    handleError,
    handleScheduleLock,
    handleScheduleRelease,
    handleScheduleUpdate,
  ]);

  useMount(() =>
    joinChannel<ScheduleLockEvent>({
      channelTopic,
      onChannelJoinSuccess: handleScheduleLock,
    }),
  );

  useUnmount(() => {
    leaveChannel(channelTopic);
  });

  return children;
}
